import { MACRO_GOALS } from "../data/meals";
import { todayKey, dateKey } from "../utils/helpers";
import { getTodayRun } from "../utils/run-plan";

// Day-seeded pseudo-random — deterministic for a given seed value
function seededRandom(seed) {
  const x = Math.sin(seed * 9301 + 49297) * 233280;
  return x - Math.floor(x);
}

/**
 * Builds a frequency map of meal IDs seen in the past `days` days from mealLog.
 * Used to apply a recency penalty so recently recommended meals rank lower.
 */
function buildRecentFreq(mealLog, days = 7) {
  const freq = {};
  if (!mealLog) return freq;
  const now = new Date();
  for (let i = 1; i <= days; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    for (const entry of mealLog[key] || []) {
      if (entry.mealId && entry.mealId !== "__water__") {
        freq[entry.mealId] = (freq[entry.mealId] || 0) + 1;
      }
    }
  }
  return freq;
}

/**
 * Client-side coaching intelligence engine.
 * Analyzes all user data and produces contextual coaching messages
 * for every surface in the app. No API calls — instant.
 *
 * Surfaces:
 *  - Home: daily briefing + today's plan (workout, meals, hydration)
 *  - Active Workout: PR proximity alerts
 *  - Nutrition: macro coaching + smart meal recommendations
 */

// ─── WEIGHT CONTEXT HELPER ──────────────────────────────────────

/**
 * Builds a weight context object from target weight and weight log.
 * Used by all coaching surfaces to tailor advice based on:
 *  - Phase: cutting, gaining, maintaining, or recomp
 *  - Distance: how far from target (large, moderate, close, at target)
 *  - Trend: recent direction (losing, gaining, stalled)
 *  - Pace: weekly rate of change
 */
export function buildWeightContext(targetWeight, latestWeight, weightLog) {
  if (!targetWeight || !latestWeight) return { phase: null };

  const diff = latestWeight - targetWeight; // positive = above target
  const absDiff = Math.abs(diff);
  const phase = absDiff <= 2 ? "maintaining" : diff > 0 ? "cutting" : "gaining";
  const distance =
    absDiff <= 2 ? "at_target"
    : absDiff <= 5 ? "close"
    : absDiff <= 15 ? "moderate"
    : "far";

  // Weekly trend from weight log
  let weeklyRate = null;
  let trend = null;
  if (weightLog && weightLog.length >= 2) {
    const oldest = weightLog[weightLog.length - 1];
    const newest = weightLog[0];
    const days = (new Date(newest.date) - new Date(oldest.date)) / 86400000;
    if (days >= 5) {
      weeklyRate = ((newest.weight - oldest.weight) / days) * 7;
      trend = Math.abs(weeklyRate) < 0.3 ? "stalled" : weeklyRate < 0 ? "losing" : "gaining";
    }
  }

  // Estimated weeks to target at current pace
  let weeksToGoal = null;
  if (weeklyRate && trend) {
    const movingRight = (phase === "cutting" && trend === "losing") || (phase === "gaining" && trend === "gaining");
    if (movingRight && Math.abs(weeklyRate) > 0.2) {
      weeksToGoal = Math.round(absDiff / Math.abs(weeklyRate));
    }
  }

  // Coaching nudge based on phase + distance
  let nudge = null;
  if (distance === "at_target") {
    nudge = "You're at your target weight — stay consistent.";
  } else if (distance === "close") {
    nudge = `${absDiff.toFixed(1)} lbs from target. Almost there — stay the course.`;
  } else if (phase === "cutting" && trend === "losing" && weeksToGoal) {
    nudge = `${absDiff.toFixed(0)} lbs to go. At this pace, ~${weeksToGoal} week${weeksToGoal !== 1 ? "s" : ""} to target.`;
  } else if (phase === "cutting" && trend === "stalled") {
    nudge = `${absDiff.toFixed(0)} lbs from target. Weight has stalled — tighten the deficit or add cardio.`;
  } else if (phase === "cutting") {
    nudge = `${absDiff.toFixed(0)} lbs from your target of ${targetWeight}. Stay in your deficit.`;
  } else if (phase === "gaining" && trend === "gaining" && weeksToGoal) {
    nudge = `${absDiff.toFixed(0)} lbs to go. On track — ~${weeksToGoal} week${weeksToGoal !== 1 ? "s" : ""} to target.`;
  } else if (phase === "gaining") {
    nudge = `${absDiff.toFixed(0)} lbs from your target of ${targetWeight}. Keep the surplus going.`;
  }

  return { phase, distance, trend, weeklyRate, weeksToGoal, absDiff, nudge, targetWeight, latestWeight };
}

// ─── HOME TAB BRIEFING ──────────────────────────────────────────

export function getCoachBriefing({
  workouts,
  runs,
  mealLog,
  personalRecords,
  weightLog,
  macroGoals,
  exercises,
  todayTotals,
  currentStreak,
  targetWeight,
  weightReminderTime,
  userProfile,
  runPlan,
}) {
  const today = todayKey();
  const hour = new Date().getHours();

  // Today's status
  const hasTrained =
    workouts.some(
      (w) => dateKey(w.startTime) === today
    ) ||
    (runs || []).some(
      (r) => dateKey(r.timestamp) === today
    );
  const hasTracked = (mealLog[today] || []).filter((e) => e.mealId !== "__water__").length > 0;
  const todayWeighed = (weightLog || []).some((e) => e.date === today);

  // This week
  const weekAgo = Date.now() - 7 * 86400000;
  const weekWorkouts = workouts.filter((w) => w.startTime > weekAgo).length;
  const weekRuns = (runs || []).filter((r) => r.timestamp > weekAgo).length;

  // Muscle group recency
  const allCategories = ["Chest", "Back", "Legs", "Shoulders", "Arms", "Core"];
  const daysSince = {};
  for (const cat of allCategories) {
    for (const w of workouts) {
      const hasCat = (w.exerciseBlocks || []).some((b) => {
        const ex = exercises.find((e) => e.id === b.exerciseId);
        return ex && ex.category === cat;
      });
      if (hasCat) {
        daysSince[cat] = Math.floor((Date.now() - w.startTime) / 86400000);
        break;
      }
    }
  }

  let mostNeglected = null;
  let maxDays = 0;
  for (const [cat, days] of Object.entries(daysSince)) {
    if (days > maxDays) {
      maxDays = days;
      mostNeglected = cat;
    }
  }

  // Consecutive training days (to detect need for rest)
  let consecutiveDays = 0;
  const d = new Date();
  for (let i = 0; i < 14; i++) {
    const key = dateKey(d);
    const active =
      workouts.some(
        (w) => dateKey(w.startTime) === key
      ) ||
      (runs || []).some(
        (r) => dateKey(r.timestamp) === key
      );
    if (active) {
      consecutiveDays++;
      d.setDate(d.getDate() - 1);
    } else if (i === 0) {
      d.setDate(d.getDate() - 1);
    } else {
      break;
    }
  }

  // Nutrition
  const goals = macroGoals || MACRO_GOALS;
  const proteinGap = Math.max(
    0,
    Math.round(goals.protein - (todayTotals?.protein || 0))
  );
  const proteinPct =
    goals.protein > 0 ? (todayTotals?.protein || 0) / goals.protein : 0;

  // Recent PRs
  const recentPRs = Object.values(personalRecords).filter(
    (pr) => pr.date && Date.now() - pr.date < 7 * 86400000
  ).length;

  // Weight reminder — nudge if past reminder time and not logged today
  let weightNudge = null;
  if (weightReminderTime && !todayWeighed) {
    const [rh, rm] = weightReminderTime.split(":").map(Number);
    if (hour > rh || (hour === rh && new Date().getMinutes() >= (rm || 0))) {
      weightNudge = "Time to log your weight.";
    }
  }

  // Target weight context — informs coaching tone and advice
  const latestWeight = weightLog?.[0]?.weight ?? null;
  const weightCtx = buildWeightContext(targetWeight, latestWeight, weightLog);

  // Profile-aware context
  const goal = userProfile?.primaryGoal;
  const experience = userProfile?.experienceLevel;
  const targetDays = userProfile?.trainingDaysPerWeek
    ? parseInt(userProfile.trainingDaysPerWeek)
    : null;

  // Run plan awareness
  const todayScheduledRun = runPlan ? getTodayRun(runPlan) : null;
  const isRunDay = todayScheduledRun && todayScheduledRun.type !== "Rest" && todayScheduledRun.type !== "Cross Train";
  const runType = todayScheduledRun?.type || null;
  const runDistance = todayScheduledRun?.distance || null;

  // Build message
  const briefing = buildMessage({
    hasTrained,
    hasTracked,
    weekWorkouts,
    weekRuns,
    currentStreak,
    consecutiveDays,
    mostNeglected,
    maxDays,
    proteinGap,
    proteinPct,
    recentPRs,
    totalWorkouts: workouts.length,
    hour,
    allCategories,
    daysSince,
    goal,
    experience,
    targetDays,
    weightCtx,
    isRunDay,
    runType,
    runDistance,
  });

  // Combine nudges — weight reminder takes priority
  const nudge = weightNudge || briefing.nudge || weightCtx.nudge;

  return {
    ...briefing,
    nudge,
    todayStatus: { hasTrained, hasTracked, todayWeighed },
  };
}

function buildMessage({
  hasTrained,
  hasTracked,
  weekWorkouts,
  weekRuns,
  currentStreak,
  consecutiveDays,
  mostNeglected,
  maxDays,
  proteinGap,
  proteinPct,
  recentPRs,
  totalWorkouts,
  hour,
  allCategories,
  daysSince,
  goal,
  experience,
  targetDays,
  weightCtx,
  isRunDay,
  runType,
  runDistance,
}) {
  // Goal-aware helpers
  const goalLabel =
    goal === "lose_fat" ? "fat loss"
    : goal === "build_muscle" ? "muscle building"
    : goal === "get_stronger" ? "strength"
    : goal === "general_fitness" ? "fitness"
    : goal === "athletic" ? "performance"
    : null;

  const isBeginnerish = experience === "beginner" || experience === "some_experience";
  const onTrack = targetDays ? weekWorkouts >= targetDays : weekWorkouts >= 3;
  const behindSchedule = targetDays ? weekWorkouts < targetDays - 1 : false;

  // Run plan context string for embedding in messages
  const runLabel = runType && runDistance
    ? `${runType} run (${runDistance})`
    : runType
      ? `${runType} run`
      : "a run";

  // Brand new user
  if (totalWorkouts === 0) {
    const goalHint = goalLabel ? ` Your ${goalLabel} program starts now.` : "";
    return {
      message:
        `Let's get started. Generate your first workout or log a meal — I'll learn your patterns and coach from day one.${goalHint}`,
      nudge: null,
    };
  }

  // Needs rest — experience-aware threshold (but respect run plan if it says run today)
  const restThreshold = isBeginnerish ? 3 : 4;
  if (consecutiveDays >= restThreshold && !hasTrained && !isRunDay) {
    const restMsg = isBeginnerish
      ? `${consecutiveDays} days in a row is solid for where you are. Recovery is when your body adapts — take today off.`
      : `${consecutiveDays} days straight. Your body builds muscle during rest, not during training. Take today off — focus on nutrition and recovery.`;
    return {
      message: restMsg,
      nudge: !hasTracked ? "Track your meals to fuel recovery." : null,
    };
  }

  // Run day — haven't done it yet (highest priority for run plan users)
  if (isRunDay && !hasTrained) {
    const streakNote = currentStreak > 0 ? ` ${currentStreak} day streak.` : "";
    const timeNote = hour >= 14 ? " Don't let the day slip." : "";
    return {
      message: `Today's plan: ${runLabel}.${streakNote}${timeNote}`,
      nudge:
        !hasTracked && hour >= 10
          ? `You're ${proteinGap}g short on protein — fuel the run.`
          : null,
    };
  }

  // Recent PRs — momentum
  if (recentPRs > 0 && !hasTrained) {
    const goalNudge = goal === "get_stronger" ? " Strength is trending up." : "";
    return {
      message: `You hit ${recentPRs} PR${recentPRs > 1 ? "s" : ""} this week. Momentum is real — keep showing up.${goalNudge}${mostNeglected && maxDays >= 5 ? ` ${mostNeglected} hasn't been hit in ${maxDays} days.` : ""}`,
      nudge:
        !hasTracked && hour >= 10
          ? `You're ${proteinGap}g short on protein.`
          : null,
    };
  }

  // Neglected muscle group
  if (mostNeglected && maxDays >= 7) {
    return {
      message: `${mostNeglected} hasn't been trained in ${maxDays} days. ${weekWorkouts > 0 ? `${weekWorkouts} session${weekWorkouts !== 1 ? "s" : ""} this week — make the next one count.` : "Time to get back at it."}`,
      nudge:
        !hasTracked && hour >= 10 ? "Don't forget to track your meals." : null,
    };
  }

  // Strong week (meeting or exceeding target days)
  if (onTrack && weekWorkouts + weekRuns >= 4) {
    const neglected = allCategories.filter((c) => !daysSince[c] || daysSince[c] >= 7);
    const targetHit = targetDays ? ` You're hitting your ${targetDays}x/week target.` : "";
    return {
      message: `Strong week — ${weekWorkouts} workout${weekWorkouts !== 1 ? "s" : ""}${weekRuns > 0 ? `, ${weekRuns} run${weekRuns !== 1 ? "s" : ""}` : ""}.${targetHit} ${neglected.length > 0 ? `${neglected.slice(0, 2).join(" and ")} could use attention.` : "Well-rounded training."}`,
      nudge:
        proteinPct < 0.5 && hour >= 12
          ? `Protein is low — ${proteinGap}g to go.`
          : null,
    };
  }

  // Afternoon, haven't trained
  if (!hasTrained && hour >= 14) {
    const scheduleNote = behindSchedule
      ? ` ${targetDays - weekWorkouts} more session${targetDays - weekWorkouts !== 1 ? "s" : ""} to hit your weekly goal.`
      : "";
    return {
      message: `${currentStreak > 0 ? `${currentStreak} day streak on the line.` : "No activity today yet."} ${weekWorkouts > 0 ? `${weekWorkouts} session${weekWorkouts !== 1 ? "s" : ""} this week.` : "The week's still young."}${scheduleNote}${mostNeglected && maxDays >= 5 ? ` ${mostNeglected} is overdue.` : ""}`,
      nudge: !hasTracked ? "Track your meals to stay on top of macros." : null,
    };
  }

  // Trained but didn't track
  if (hasTrained && !hasTracked) {
    let nutritionHint;
    if (weightCtx?.phase === "cutting" && weightCtx.distance === "far") {
      nutritionHint = `You have ${weightCtx.absDiff.toFixed(0)} lbs to lose — every meal matters. Track them.`;
    } else if (weightCtx?.phase === "cutting") {
      nutritionHint = "Tracking is everything during a cut.";
    } else if (weightCtx?.phase === "gaining") {
      nutritionHint = "You need a surplus to grow — log your meals to stay on track.";
    } else if (goal === "lose_fat") {
      nutritionHint = "Tracking is everything during a cut.";
    } else if (goal === "build_muscle") {
      nutritionHint = "You need the calories to grow — log them.";
    } else {
      nutritionHint = "Log your meals so I can track your recovery nutrition.";
    }
    return {
      message: `Good session. Now fuel the work — ${nutritionHint}`,
      nudge: null,
    };
  }

  // Both done
  if (hasTrained && hasTracked) {
    const phaseNote = weightCtx?.phase === "cutting" && weightCtx.trend === "losing"
      ? " Cut is on track."
      : weightCtx?.phase === "cutting" && weightCtx.trend === "stalled"
        ? " Weight is stalling — consider tightening the deficit."
        : "";
    return {
      message: `Training done, nutrition tracked. ${proteinPct >= 0.8 ? "Protein is on point." : `Push for ${proteinGap}g more protein.`}${phaseNote}${currentStreak > 2 ? ` ${currentStreak} day streak.` : ""}`,
      nudge: null,
    };
  }

  // Default
  const paceNote = behindSchedule
    ? ` You're behind your ${targetDays}x/week target.`
    : targetDays && onTrack
      ? ` On pace for ${targetDays}x this week.`
      : "";
  return {
    message: `${weekWorkouts} session${weekWorkouts !== 1 ? "s" : ""} this week.${paceNote}${currentStreak > 0 ? ` ${currentStreak} day streak.` : ""} Stay consistent.`,
    nudge: null,
  };
}

// ─── ACTIVE WORKOUT: PR PROXIMITY ───────────────────────────────

export function getWorkoutCoaching({
  exerciseBlocks,
  exercises,
  personalRecords,
}) {
  const tips = {};

  for (const block of exerciseBlocks) {
    const ex = exercises.find((e) => e.id === block.exerciseId);
    if (!ex || ex.unit === "BW" || ex.unit === "sec") continue;

    const pr = personalRecords[block.exerciseId];
    if (!pr) {
      tips[block.exerciseId] = {
        type: "first",
        message: "No PR yet — set one today.",
      };
      continue;
    }

    const maxWeight = Math.max(
      ...block.sets.map((s) => parseFloat(s.weight) || 0)
    );

    if (maxWeight > 0) {
      const diff = pr.weight - maxWeight;
      if (diff <= 0) {
        tips[block.exerciseId] = {
          type: "at_pr",
          message: "You're at your PR weight. Push for a new record.",
        };
      } else if (diff <= 10) {
        tips[block.exerciseId] = {
          type: "close",
          message: `${diff} lbs from your PR (${pr.weight} lbs).`,
        };
      }
    }
  }

  return tips;
}

// ─── NUTRITION TAB COACHING ─────────────────────────────────────

export function getNutritionCoaching({ todayTotals, macroGoals, todayMeals, userProfile, weightCtx }) {
  const goals = macroGoals || MACRO_GOALS;
  const hour = new Date().getHours();
  const goal = userProfile?.primaryGoal;
  const isCutting = weightCtx?.phase === "cutting";
  const isGaining = weightCtx?.phase === "gaining";

  if (todayMeals.length === 0) {
    if (hour >= 10) {
      let hint;
      if (isCutting && weightCtx.distance === "far") {
        hint = `You have ${weightCtx.absDiff.toFixed(0)} lbs to lose — tracking every meal keeps the deficit real.`;
      } else if (isCutting || goal === "lose_fat") {
        hint = "Tracking keeps your deficit honest.";
      } else if (isGaining || goal === "build_muscle") {
        hint = "You can't build without fuel — log what you eat.";
      } else {
        hint = "Track as you eat — it takes 5 seconds and I can coach you better with the data.";
      }
      return { message: `No meals logged yet. ${hint}` };
    }
    return null;
  }

  const proteinGap = Math.round(goals.protein - todayTotals.protein);
  const proteinPct = todayTotals.protein / goals.protein;
  const calPct = todayTotals.calories / goals.calories;

  if (proteinPct >= 1 && calPct >= 0.8) {
    let praise;
    if (isCutting) {
      praise = "Protein goal hit while staying in your deficit. That's how you lose fat and keep muscle.";
    } else if (isGaining) {
      praise = "Protein goal hit — recovery and growth are covered.";
    } else {
      praise = "Protein goal hit. Solid nutrition day.";
    }
    return { message: praise };
  }

  if (proteinPct < 0.5 && hour >= 15) {
    const urgency = isCutting
      ? " High protein protects muscle during a cut."
      : "";
    return {
      message: `Behind on protein — ${proteinGap}g to go. Prioritize protein in your next meal.${urgency}`,
    };
  }

  if (proteinGap > 0 && proteinGap <= 30) {
    return {
      message: `Almost there — ${proteinGap}g protein left. A shake or chicken breast closes the gap.`,
    };
  }

  if (calPct > 1.1) {
    let overMsg;
    const overBy = Math.round(todayTotals.calories - goals.calories);
    if (isCutting) {
      overMsg = `Over calorie target by ${overBy} kcal. ${weightCtx.distance === "far" ? "Consistency matters more than perfection — get back on track tomorrow." : "Stay tight on the deficit — one meal doesn't break it, but track everything."}`;
    } else if (goal === "lose_fat") {
      overMsg = `Over calorie target by ${overBy} kcal. Stay tight on the deficit — one meal doesn't break it, but track everything.`;
    } else {
      overMsg = `You're ${overBy} calories over target. Not a crisis — just be mindful for the rest of the day.`;
    }
    return { message: overMsg };
  }

  if (proteinGap > 0) {
    const calGap = Math.round(goals.calories - todayTotals.calories);
    if (calGap > 400) {
      return {
        message: `You've got ${proteinGap}g of protein and ${calGap} calories left to work with. Plenty of room — pick something you'll enjoy that's protein-forward.`,
      };
    }
    if (calGap > 200) {
      return {
        message: `${proteinGap}g protein left. You have about ${calGap} calories of runway — lean protein will close both gaps.`,
      };
    }
    return {
      message: `${proteinGap}g protein to go. You're close on calories, so go lean — greek yogurt, chicken, or a protein shake.`,
    };
  }

  return null;
}

// ─── 5-DAY WORKOUT FORECAST ─────────────────────────────────────

/**
 * Projects the next 5 days of training based on muscle recency,
 * recovery needs, training frequency, and periodization state.
 *
 * Returns an array of 5 forecast day objects with muscle recovery
 * status that updates dynamically based on what the user trains today.
 */
export function getWorkoutForecast({
  workouts,
  exercises,
  userProfile,
  runs,
  todayFocus,
}) {
  const allCategories = ["Chest", "Back", "Legs", "Shoulders", "Arms", "Core"];
  const experience = userProfile?.experienceLevel;
  const isBeginnerish = experience === "beginner" || experience === "some_experience";
  const targetDays = userProfile?.trainingDaysPerWeek
    ? parseInt(userProfile.trainingDaysPerWeek)
    : 4;

  // Build muscle recency map (days since each group was trained)
  const muscleLastTrained = {};
  for (const cat of allCategories) {
    for (const w of [...workouts].sort((a, b) => b.startTime - a.startTime)) {
      const hasCat = (w.exerciseBlocks || []).some((b) => {
        const ex = exercises.find((e) => e.id === b.exerciseId);
        return ex && ex.category === cat;
      });
      if (hasCat) {
        muscleLastTrained[cat] = Math.floor((Date.now() - w.startTime) / 86400000);
        break;
      }
    }
  }

  // If today's focus is set (from the generator), simulate that training
  if (todayFocus && allCategories.includes(todayFocus)) {
    muscleLastTrained[todayFocus] = 0;
  }

  // Check consecutive training days to detect rest needs
  let consecutiveDays = 0;
  const d = new Date();
  for (let i = 0; i < 14; i++) {
    const key = dateKey(d);
    const active =
      workouts.some((w) => dateKey(w.startTime) === key) ||
      (runs || []).some((r) => dateKey(r.timestamp) === key);
    if (active || (i === 0 && todayFocus)) {
      consecutiveDays++;
      d.setDate(d.getDate() - 1);
    } else if (i === 0) {
      d.setDate(d.getDate() - 1);
    } else {
      break;
    }
  }

  // Count sessions this week for frequency management
  const weekAgo = Date.now() - 7 * 86400000;
  let weekSessions = workouts.filter((w) => w.startTime > weekAgo).length;
  if (todayFocus) weekSessions++;

  const restThreshold = isBeginnerish ? 3 : 4;
  const forecast = [];
  let simConsecutive = consecutiveDays;
  let simWeekSessions = weekSessions;

  // Simulate each muscle group's recovery day-by-day
  const simRecency = { ...muscleLastTrained };

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  for (let dayOffset = 1; dayOffset <= 5; dayOffset++) {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + dayOffset);
    const dayName = dayNames[futureDate.getDay()];
    const dateStr = `${futureDate.getMonth() + 1}/${futureDate.getDate()}`;

    // Advance all muscle recency by 1 day
    for (const cat of allCategories) {
      if (simRecency[cat] !== undefined) {
        simRecency[cat]++;
      }
    }

    // Determine if this should be a rest day
    const needsRest = simConsecutive >= restThreshold;
    const hitWeeklyTarget = simWeekSessions >= targetDays;

    if (needsRest || hitWeeklyTarget) {
      // Rest day
      const muscleStatus = allCategories.map((cat) => {
        const days = simRecency[cat];
        return {
          group: cat,
          daysSince: days ?? null,
          status: days === undefined ? "untrained"
            : days <= 1 ? "recovering"
            : days <= 2 ? "fresh"
            : "ready",
        };
      });

      forecast.push({
        date: dateStr,
        dayName,
        type: needsRest ? "rest" : "active_rest",
        suggestedFocus: null,
        reasoning: needsRest
          ? `${simConsecutive} days in a row — recovery day`
          : `${simWeekSessions}/${targetDays} sessions done this week`,
        muscleStatus,
      });

      simConsecutive = 0;
      continue;
    }

    // Training day — pick the most overdue muscle group that's recovered (48h+)
    const candidates = allCategories
      .filter((cat) => {
        const days = simRecency[cat];
        return days === undefined || days >= 2; // 48h+ recovery
      })
      .sort((a, b) => {
        const daysA = simRecency[a] ?? 999;
        const daysB = simRecency[b] ?? 999;
        return daysB - daysA;
      });

    const focus = candidates[0] || allCategories[0];
    const focusDays = simRecency[focus];

    // Build muscle status
    const muscleStatus = allCategories.map((cat) => {
      const days = simRecency[cat];
      return {
        group: cat,
        daysSince: days ?? null,
        status: days === undefined ? "untrained"
          : days <= 1 ? "recovering"
          : days <= 2 ? "fresh"
          : "ready",
      };
    });

    forecast.push({
      date: dateStr,
      dayName,
      type: "training",
      suggestedFocus: focus,
      reasoning: focusDays !== undefined
        ? `${focus} last trained ${focusDays}d ago — most overdue`
        : `${focus} hasn't been trained yet`,
      muscleStatus,
    });

    // Simulate: mark focus group as just trained
    simRecency[focus] = 0;
    simConsecutive++;
    simWeekSessions++;
  }

  return forecast;
}

// ─── SMART MEAL RECOMMENDATIONS ─────────────────────────────────

/**
 * Recommends 2-3 meals from the library that best fill current macro gaps.
 * Time-aware: suggests breakfast meals in the morning, lunch/dinner later.
 * Adapts as user logs meals throughout the day.
 */
export function getMealRecommendations({
  allMeals,
  todayTotals,
  todayMeals,
  macroGoals,
  mealLog,
}) {
  const goals = macroGoals || MACRO_GOALS;
  const hour = new Date().getHours();

  const calGap = Math.max(0, goals.calories - (todayTotals?.calories || 0));
  const proGap = Math.max(0, goals.protein - (todayTotals?.protein || 0));

  // All goals met
  if (calGap <= 100 && proGap <= 10) return [];

  // Time-appropriate categories
  const categories = [];
  if (hour < 11) categories.push("Breakfast");
  else if (hour < 16) categories.push("Lunch/Dinner", "Snack");
  else categories.push("Lunch/Dinner", "Snack");

  // Already-logged meal IDs (avoid suggesting what they already had today)
  const loggedIds = new Set(todayMeals.map((e) => e.mealId));

  // Recency map: de-rank meals seen in the last 7 days
  const recentFreq = buildRecentFreq(mealLog, 7);
  const dayNum = Math.floor(Date.now() / 86400000);

  // Score each eligible meal
  const scored = allMeals
    .filter((m) => categories.includes(m.category) && !loggedIds.has(m.id))
    .map((meal) => {
      const proScore = proGap > 0 ? Math.min(1, meal.protein / proGap) : 0;
      const calFit = calGap > 0
        ? (meal.calories <= calGap ? 1 : Math.max(0, 1 - (meal.calories - calGap) / calGap))
        : 0;
      const proDensity = meal.calories > 0 ? meal.protein / meal.calories : 0;
      let score = proScore * 0.45 + calFit * 0.35 + proDensity * 0.2;

      // Recency penalty
      const recentCount = recentFreq[meal.id] || 0;
      if (recentCount > 0) score *= Math.max(0.25, 1 - recentCount * 0.2);

      // Small day-seeded nudge so the same tier of meals rotates daily
      score += seededRandom(dayNum + meal.id.charCodeAt(meal.id.length - 1)) * 0.05;

      return { meal, score };
    })
    .sort((a, b) => b.score - a.score);

  // Return top 3
  return scored.slice(0, 3).map((s) => s.meal);
}

// ─── DAILY PLAN (HOME TAB) ──────────────────────────────────────

/**
 * Picks the best macro-fitting meal for a given category from the pool.
 * Applies a recency penalty for meals seen recently, and uses a day-seeded
 * random selection among the top candidates for natural daily variety.
 *
 * @param {Array}  pool        - All available meals
 * @param {string} category    - "Breakfast" or "Lunch/Dinner"
 * @param {Set}    excludeIds  - Meal IDs to skip entirely
 * @param {number} proGap      - Remaining protein goal
 * @param {number} calGap      - Remaining calorie goal
 * @param {Object} recentFreq  - { mealId: count } for past 7 days
 * @param {number} slotSeed    - Day-based seed for variety (different per slot)
 */
function pickBestMeal(pool, category, excludeIds, proGap, calGap, recentFreq = {}, slotSeed = 0) {
  const candidates = pool.filter(
    (m) => m.category === category && !excludeIds.has(m.id)
  );
  if (candidates.length === 0) return null;

  // Score all candidates
  const scored = candidates.map((m) => {
    let score;
    if (proGap <= 0 && calGap <= 0) {
      // Goals met — rank by protein density
      score = m.protein / Math.max(1, m.calories);
    } else {
      const proScore = proGap > 0 ? Math.min(1, m.protein / Math.max(1, proGap)) : 0.5;
      const calFit = calGap > 0
        ? (m.calories <= calGap ? 1 : Math.max(0, 1 - (m.calories - calGap) / calGap))
        : 0.5;
      score = proScore * 0.6 + calFit * 0.4;
    }

    // Recency penalty: meals seen recently get de-ranked
    // Each appearance in the last 7 days reduces score by 25%, down to a 25% floor
    const recentCount = recentFreq[m.id] || 0;
    if (recentCount > 0) {
      score *= Math.max(0.25, 1 - recentCount * 0.25);
    }

    return { meal: m, score };
  });

  scored.sort((a, b) => b.score - a.score);

  // Pick from the top candidates using a day-seeded random index.
  // This ensures the same great-macro meals don't win every single day —
  // they rotate fairly among similarly-ranked options.
  const poolSize = Math.min(scored.length, 5);
  const idx = Math.floor(seededRandom(slotSeed) * poolSize);
  return scored[idx].meal;
}

/**
 * Get N alternative meal suggestions for a given slot (for the swap UI).
 * Applies recency penalty so recently eaten meals don't dominate the swap panel.
 */
export function getAlternativeMeals({ allMeals, slot, currentMealId, excludeIds, proGap, calGap, count = 4, mealLog }) {
  const category = slot === "Breakfast" ? "Breakfast" : "Lunch/Dinner";
  const skip = new Set(excludeIds || []);
  if (currentMealId) skip.add(currentMealId);

  const recentFreq = buildRecentFreq(mealLog, 7);

  const candidates = allMeals.filter(
    (m) => m.category === category && !skip.has(m.id)
  );

  const scored = candidates.map((m) => {
    const proScore = proGap > 0 ? Math.min(1, m.protein / Math.max(1, proGap)) : 0.5;
    const calFit = calGap > 0
      ? (m.calories <= calGap ? 1 : Math.max(0, 1 - (m.calories - calGap) / calGap))
      : 0.5;
    let score = proScore * 0.6 + calFit * 0.4;

    // Recency penalty — de-rank meals seen recently
    const recentCount = recentFreq[m.id] || 0;
    if (recentCount > 0) score *= Math.max(0.25, 1 - recentCount * 0.2);

    return { meal: m, score };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, count).map((s) => s.meal);
}

/**
 * Builds a full daily plan: training focus, 3 meals (B/L/D), hydration.
 * ALWAYS returns exactly 3 meal slots. Each slot is either logged or a suggestion.
 */
export function getDailyPlan({
  workouts,
  exercises,
  allMeals,
  todayTotals,
  todayMeals,
  macroGoals,
  userProfile,
  hasTodayWorkout,
  mealLog,
}) {
  const goals = macroGoals || MACRO_GOALS;

  // Build recency map from last 7 days so recently-seen meals get de-ranked
  const recentFreq = buildRecentFreq(mealLog, 7);
  // Day number for seeded variety — different every calendar day
  const dayNum = Math.floor(Date.now() / 86400000);

  // ─── Training suggestion ───
  const allCategories = ["Chest", "Back", "Legs", "Shoulders", "Arms", "Core"];
  const daysSince = {};
  for (const cat of allCategories) {
    for (const w of workouts) {
      const hasCat = (w.exerciseBlocks || []).some((b) => {
        const ex = exercises.find((e) => e.id === b.exerciseId);
        return ex && ex.category === cat;
      });
      if (hasCat) {
        daysSince[cat] = Math.floor((Date.now() - w.startTime) / 86400000);
        break;
      }
    }
  }
  const overdue = allCategories
    .filter((c) => daysSince[c] !== undefined)
    .sort((a, b) => (daysSince[b] || 0) - (daysSince[a] || 0));
  const neverTrained = allCategories.filter((c) => daysSince[c] === undefined);
  const trainingFocus =
    neverTrained.length > 0
      ? neverTrained[0]
      : overdue.length > 0
        ? overdue[0]
        : null;

  // ─── Meal plan: always 3 slots (Breakfast / Lunch / Dinner) ───
  const PLAN_SLOTS = [
    { slot: "Breakfast", category: "Breakfast" },
    { slot: "Lunch", category: "Lunch/Dinner" },
    { slot: "Dinner", category: "Lunch/Dinner" },
  ];

  const loggedIds = new Set((todayMeals || []).map((e) => e.mealId));
  let proGap = Math.max(0, goals.protein - (todayTotals?.protein || 0));
  let calGap = Math.max(0, goals.calories - (todayTotals?.calories || 0));

  // Detect what's already logged for each slot by meal category (not time window).
  // Sort by timestamp so the earliest logged meal fills each slot.
  const sortedMeals = [...(todayMeals || [])].sort((a, b) => (a.ts || 0) - (b.ts || 0));
  const loggedBySlot = {};
  const unmatchedEntries = [];
  for (const entry of sortedMeals) {
    if (entry.mealId === "__water__") continue;
    const meal = (allMeals || []).find((m) => m.id === entry.mealId);
    if (!meal) continue;
    // Match by meal category so meals logged at any time of day are recognised
    if (meal.category === "Breakfast" && !loggedBySlot["Breakfast"]) {
      loggedBySlot["Breakfast"] = meal;
    } else if (meal.category === "Lunch/Dinner" && !loggedBySlot["Lunch"]) {
      loggedBySlot["Lunch"] = meal;
    } else if (meal.category === "Lunch/Dinner" && !loggedBySlot["Dinner"]) {
      loggedBySlot["Dinner"] = meal;
    } else if (meal.category !== "Breakfast" && meal.category !== "Lunch/Dinner" && meal.category !== "Snack") {
      // Quick Add / coach-created meals — assign to slot by time of day
      unmatchedEntries.push({ entry, meal });
    }
    // Snacks don't occupy a B/L/D plan slot
  }
  // Fallback: assign Quick Add / coach meals to empty slots by time
  for (const { entry, meal } of unmatchedEntries) {
    const h = new Date(entry.ts).getHours();
    const timeSlot = h < 10 ? "Breakfast" : h < 15 ? "Lunch" : "Dinner";
    if (!loggedBySlot[timeSlot]) {
      loggedBySlot[timeSlot] = meal;
    }
  }

  const mealPlan = [];
  const usedIds = new Set();
  // Per-slot seed offsets keep lunch/dinner from picking the same index
  const SLOT_SEEDS = { Breakfast: 0, Lunch: 7777, Dinner: 31337 };

  for (const { slot, category } of PLAN_SLOTS) {
    // If already logged for this slot, show what they had.
    // Don't subtract from proGap/calGap — todayTotals already accounts for all
    // logged meals, so the gaps were initialised with those meals included.
    if (loggedBySlot[slot]) {
      mealPlan.push({ slot, meal: loggedBySlot[slot], logged: true });
      usedIds.add(loggedBySlot[slot].id);
      continue;
    }

    const slotSeed = dayNum + (SLOT_SEEDS[slot] || 0);

    // Suggest the best macro-fitting meal — combine loggedIds + usedIds to avoid dupes
    const exclude = new Set([...loggedIds, ...usedIds]);
    let best = pickBestMeal(allMeals || [], category, exclude, proGap, calGap, recentFreq, slotSeed);

    if (!best) {
      // Fallback: pick ANY meal from the category (ignoring logged filter)
      best = pickBestMeal(allMeals || [], category, usedIds, proGap, calGap, recentFreq, slotSeed);
    }

    if (!best) {
      // Final fallback: pick any meal from the category ignoring all exclusions
      const anyCandidate = (allMeals || []).filter((m) => m.category === category);
      if (anyCandidate.length > 0) best = anyCandidate[0];
    }

    if (best) {
      mealPlan.push({ slot, meal: best, logged: false });
      usedIds.add(best.id);
      // Update remaining gaps so next meal targets what's left
      proGap = Math.max(0, proGap - (best.protein || 0));
      calGap = Math.max(0, calGap - (best.calories || 0));
    }
  }

  // Safety net: ensure exactly 3 slots — fill any missing ones
  for (const { slot, category } of PLAN_SLOTS) {
    if (mealPlan.some((mp) => mp.slot === slot)) continue;
    const pool = (allMeals || []).filter(
      (m) => m.category === category && !usedIds.has(m.id)
    );
    const fallback = pool[0] || (allMeals || []).find((m) => m.category === category);
    if (fallback) {
      mealPlan.push({ slot, meal: fallback, logged: false });
      usedIds.add(fallback.id);
    }
  }

  // ─── Hydration ───
  // Standard 8-glasses (64 oz) base; add 16 oz on workout days (10 glasses).
  const hydrationOz = 64 + (hasTodayWorkout ? 16 : 0);

  // Goal + weight-phase-aware training tip
  const goal = userProfile?.primaryGoal;
  const experience = userProfile?.experienceLevel;
  let trainingTip = null;
  if (trainingFocus) {
    const wc = userProfile?._weightCtx;
    if (wc?.phase === "cutting" && wc.distance === "far") {
      trainingTip = "Prioritize compound lifts and keep intensity up — protect muscle while cutting.";
    } else if (wc?.phase === "cutting") {
      trainingTip = "Maintain your strength during the cut. Don't chase volume — chase intensity.";
    } else if (wc?.phase === "gaining") {
      trainingTip = "You're in a surplus — push for progressive overload and earn the calories.";
    } else if (goal === "lose_fat") {
      trainingTip = "Keep rest periods short to maintain heart rate.";
    } else if (goal === "get_stronger" || goal === "build_muscle") {
      trainingTip = experience === "beginner"
        ? "Focus on form before adding weight."
        : "Push for progressive overload today.";
    }
  }

  return {
    trainingFocus,
    daysSinceFocus: trainingFocus ? daysSince[trainingFocus] : null,
    trainingTip,
    mealPlan,
    hydrationOz,
    macroSnapshot: {
      proteinRemaining: Math.round(proGap),
      caloriesRemaining: Math.round(calGap),
    },
  };
}
